// TODO: Guard state that checks regularly for failures and/or no-loads

import { ConsentPlugin } from './types';
import { LoggerSkeleton } from '../../types';
import { ObserverFunction } from '../../types/Observable';
import {
  ConsentCategory,
  ConsentEvent,
  ConsentState,
  ConsentStateObserverFunction,
  ConsentType,
  ShopConfig,
} from '../types';

enum OneTrustConsentCategory {
  STRICTLY_NECESSARY = 'C0001',
  PERFORMANCE = 'C0002',
  FUNCTIONAL = 'C0003',
  TARGETING = 'C0004',
  SOCIAL_MEDIA = 'C0005',
}

class OneTrustConsentPlugin implements ConsentPlugin<ConsentEvent, ConsentState> {
  #consentState: ConsentState;
  #observers: Map<ConsentEvent, ConsentStateObserverFunction[] | undefined>;
  #isInitialized = false;
  #window: Window;
  #shopConfig: ShopConfig;
  #logger: LoggerSkeleton;

  constructor(window: Window, shopConfig: ShopConfig, logger: LoggerSkeleton) {
    this.#window = window;
    this.#shopConfig = shopConfig;
    this.#logger = logger;

    this.#consentState = {
      [ConsentCategory.FUNCTIONAL]: this.#shopConfig.defaultScriptStatus,
      [ConsentCategory.PERFORMANCE]: this.#shopConfig.defaultScriptStatus,
      [ConsentCategory.SOCIAL_MEDIA]: this.#shopConfig.defaultScriptStatus,
      [ConsentCategory.STRICTLY_NECESSARY]: ConsentType.CONSENTED,
      [ConsentCategory.TARGETING]: this.#shopConfig.defaultScriptStatus,
    };
    this.#observers = new Map<ConsentEvent, ConsentStateObserverFunction[] | undefined>();
  }

  initialize(): void {
    if (!this.#isInitialized) {
      try {
        this.loadOneTrustStubScript();
        if (this.#shopConfig.shopID == 'EU' || this.#shopConfig.shopID == 'UK') {
          this.loadOneTrustAutoBlockScript();
        }

        this.#window.OptanonWrapper = this.optanonWrapper.bind(this);
      } catch (e: unknown) {
        this.#isInitialized = false;

        this.#logger.error('class=OneTrustConsentPlugin | #initialize', {}, e);
      }
      this.#isInitialized = true;
    }
  }

  optanonWrapper(): void {
    try {
      this.#logger.debug('class=OneTrustConsentPlugin | #optanonWrapper');

      this.#consentState = this.generateConsentStateFromActiveGroups(
        this.#window.OnetrustActiveGroups
      );

      this.notify(ConsentEvent.CONSENT_STATE_CHANGE, this.#consentState);

      // START legacy OT integration
      this.#window.ALLBIRDS = this.#window.ALLBIRDS || {};
      if (this.#window.ALLBIRDS.onConsentInitialized) {
        this.#window.ALLBIRDS.onConsentInitialized();
      }
      this.#window.ALLBIRDS.consentInitialized = true;
      // END legacy OT integration
    } catch (e: unknown) {
      console.log(e);
      this.#logger.error('class=OneTrustConsentPlugin | #optanonWrapper', {}, e);
    }
  }

  /**
   *
   * @param activeGroups {string} Expected to be a string of comma-separated consent groups. Any string present is
   * considered as "consent given."
   * @private
   */
  generateConsentStateFromActiveGroups(activeGroups: string): ConsentState {
    this.#logger.debug('class=OneTrustConsentPlugin | #generateConsentStateFromActiveGroups', {
      activeGroups,
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any = {};
    for (const consentKey in OneTrustConsentCategory) {
      let consentType: ConsentType = ConsentType.DENIED;
      if (
        activeGroups.includes(
          OneTrustConsentCategory[consentKey as keyof typeof OneTrustConsentCategory]
        )
      ) {
        consentType = ConsentType.CONSENTED;
      }

      data[consentKey] = consentType;
    }

    return data;
  }

  loadOneTrustStubScript(): void {
    this.#logger.debug('class=OneTrustConsentPlugin | #loadOneTrustStubScript');
    try {
      const stubScript = this.#window.document.createElement('script');
      stubScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
      stubScript.type = 'text/javascript';
      stubScript.setAttribute('data-document-language', 'true');

      stubScript.setAttribute('data-domain-script', this.#shopConfig.oneTrustID);

      this.#window.document.head.appendChild(stubScript);
    } catch (e: unknown) {
      this.#logger.error('class=OneTrustConsentPlugin | #loadOneTrustStubScript', {}, e);
    }
  }

  loadOneTrustAutoBlockScript(): void {
    this.#logger.debug('class=OneTrustConsentPlugin | #loadOneTrustAutoBlockScript');
    try {
      const autoBlockScript = this.#window.document.createElement('script');
      autoBlockScript.src = `https://cdn.cookielaw.org/consent/${this.#shopConfig.oneTrustID}}/OtAutoBlock.js`;
      autoBlockScript.type = 'text/javascript';

      this.#window.document.head.appendChild(autoBlockScript);
    } catch (e: unknown) {
      this.#logger.error('class=OneTrustConsentPlugin | #loadOneTrustAutoBlockScript', {}, e);
    }
  }

  notify(event: ConsentEvent, data: ConsentState): void {
    this.#logger.debug('class=OneTrustConsentPlugin | #notify', {
      event,
      data,
    });
    const observers: ConsentStateObserverFunction[] | undefined = this.#observers.get(event);
    observers?.forEach(observer => observer(data));
  }

  subscribe(event: ConsentEvent, observer: ObserverFunction<ConsentState>): void {
    this.#logger.debug('class=OneTrustConsentPlugin | #subscribe', {
      event,
      observer,
    });
    const observers: ConsentStateObserverFunction[] | undefined = this.#observers.get(event) || [
      observer,
    ];
    this.#observers.set(event, observers);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  unsubscribe(event: ConsentEvent, observer: ObserverFunction<ConsentState>): void {
    // TODO: Implement!
  }

  getConsentState(): ConsentState {
    return Object.freeze(this.#consentState);
  }
}

export default OneTrustConsentPlugin;
export { OneTrustConsentCategory };
